import React, { useLayoutEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Stack, Icon } from '@fashionnovateam/nova-ui-toolkit';
import cn from 'classnames';
import {
  PurchaseOrder,
  BulkProdSubStage,
} from '../../Lib/Types/purchase-order';
import {
  HangtagModal,
  UnitLabelModal,
} from '../ReusableComponents/NewDesign/Modals/PrintLabelModal';
import ConfirmationModal from '../ReusableComponents/Modals/ConfirmationModal/ConfirmationModal';
import { PoCardFooter } from './PoFooter';
import { PoCardHeaderButtons } from './PoCardButtons';
import { PoCardInfo } from './PoCardInfo';
import { PoImage } from './PoImage';
import { User } from '../../Lib/Api/vendor';
import { Prop65Drawer } from '../PurchaseOrderDetail/Prop65Drawer';
import toast from '../Toast/Toast';
import { useSetProp65 } from '../../Lib/Hooks/purchase-orders.hooks';
import UserAPI from '../../Lib/Api/user';
import { PurchaseOrderRevisionDrawer } from './Revision/PurchaseOrderRevisionDrawer';

export type PoCardProps = {
  purchaseOrder: PurchaseOrder & { isLoading?: boolean };
  userId: string;
  loading: boolean;
  details?: boolean;
  onAcceptPurchaseOrders: (user: string, transaction: string) => void;
  onRejectPurchaseOrders: (
    user: string,
    transaction: string,
    reject: string,
  ) => void;
  onConfirmPrint: () => void;
  onPoSubStageUpdate: (sub_stage: BulkProdSubStage) => void;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  perms: {
    canAccept: boolean;
    canReject: boolean;
    canPrint: boolean;
    canUpdateBulkStatus: boolean;
    canViewCost: boolean;
    canCreateRevision: boolean;
  };
  user: User;
  onProp65DrawerOpen?: () => void;
};

type PoCardState = {
  editingTransaction: string | undefined;
  printPOInformation: PurchaseOrder | undefined;
  openPrintUnitLabel: boolean;
  openPrintHangtag: boolean;
  openAccept: boolean;
  openReject: boolean;
};

export type PoDetailsProps = {
  purchaseOrder: PurchaseOrder;
  isMobile: boolean;
  userId: string;
  handleAcceptModal: () => void;
  handleRejectModal: () => void;
  handlePrintUnitLabelModal: () => void;
  handlePrintHangTagModal: () => void;
  handleOpenRevisionDrawer: () => void;
  onPoSubStageUpdate: (sub_stage: BulkProdSubStage) => void;
  perms: {
    canReject: boolean;
    canAccept: boolean;
    canPrint: boolean;
    canUpdateBulkStatus: boolean;
    canViewCost: boolean;
    canCreateRevision: boolean;
  };
  user: User;
  onProp65DrawerOpen?: () => void;
};

const calculateIsMobile = () => window.innerWidth < 1024;

function PoDetails({
  purchaseOrder,
  isMobile,
  userId,
  handleAcceptModal,
  handleRejectModal,
  handlePrintUnitLabelModal,
  handlePrintHangTagModal,
  handleOpenRevisionDrawer,
  onPoSubStageUpdate,
  perms,
  user,
  onProp65DrawerOpen,
}: PoDetailsProps) {
  return (
    <Stack
      direction="row"
      className={`group gap-6 bg-neutral-0 pb-12 ${isMobile && 'flex-row-reverse'}`}
    >
      <Stack direction="col" className="gap-5 w-1/5">
        <PoImage details isMobile={isMobile} purchaseOrder={purchaseOrder} />
      </Stack>
      <Stack direction="col" className="gap-5 w-4/5">
        <PoCardInfo
          details
          userId={userId}
          handleAcceptModal={handleAcceptModal}
          handleRejectModal={handleRejectModal}
          handlePrintUnitLabelModal={handlePrintUnitLabelModal}
          handlePrintHangTagModal={handlePrintHangTagModal}
          handleOpenRevisionDrawer={handleOpenRevisionDrawer}
          purchaseOrder={purchaseOrder}
          onPoSubStageUpdate={onPoSubStageUpdate}
          perms={perms}
          user={user}
          onProp65DrawerOpen={onProp65DrawerOpen}
        />
        <div className="border-t border-[#F8F8F8]" />
        <PoCardFooter
          perms={perms}
          details
          purchaseOrder={purchaseOrder}
          user={user}
        />
      </Stack>
    </Stack>
  );
}

export function PoCard({
  purchaseOrder,
  loading,
  details,
  userId,
  onAcceptPurchaseOrders,
  onRejectPurchaseOrders,
  onConfirmPrint,
  onPoSubStageUpdate,
  selected,
  onSelect,
  perms,
  user,
  onProp65DrawerOpen,
}: PoCardProps) {
  const queryClient = useQueryClient();
  const { mutateAsync: setProp65 } = useSetProp65();

  const [state, setState] = useState<PoCardState>({
    editingTransaction: undefined,
    printPOInformation: undefined,
    openPrintUnitLabel: false,
    openPrintHangtag: false,
    openAccept: false,
    openReject: false,
  });
  const [isMobile, setIsMobile] = useState(calculateIsMobile());
  const [isCardProp65DrawerOpen, setIsCardProp65DrawerOpen] = useState(false);
  const [cardProp65PurchaseOrder, setCardProp65PurchaseOrder] =
    useState<PurchaseOrder | null>(null);
  const [isRevisionModalOpen, setIsRevisionModalOpen] = useState(false);

  useLayoutEffect(() => {
    const handleResize = () => {
      setIsMobile(calculateIsMobile());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });
  if (!purchaseOrder) return null;

  const handleAcceptModal = () => {
    setState((prevState) => ({
      ...prevState,
      editingTransaction: purchaseOrder.po_number,
      openAccept: true,
    }));
  };

  const handleRejectModal = () => {
    setState((prevState) => ({
      ...prevState,
      editingTransaction: purchaseOrder.po_number,
      openReject: true,
    }));
  };

  const handlePrintUnitLabelModal = () => {
    setState((prevState) => ({
      ...prevState,
      printPOInformation: purchaseOrder,
      openPrintUnitLabel: true,
    }));
  };

  const handlePrintHangTagModal = () => {
    setState((prevState) => ({
      ...prevState,
      printPOInformation: purchaseOrder,
      openPrintHangtag: true,
    }));
  };

  const closeModal = () => {
    setState({
      openAccept: false,
      openReject: false,
      openPrintUnitLabel: false,
      openPrintHangtag: false,
      editingTransaction: undefined,
      printPOInformation: undefined,
    });
  };

  const confirmPrintModal = () => {
    onConfirmPrint();
    closeModal();
  };

  const acceptFromModal = () => {
    const { editingTransaction } = state;
    if (!editingTransaction) return;
    onAcceptPurchaseOrders(userId, editingTransaction);
    setTimeout(() => closeModal(), 400);
  };

  const rejectFromModal = (vendorRejectionReason: string) => {
    const { editingTransaction } = state;
    if (!editingTransaction) return;
    onRejectPurchaseOrders(userId, editingTransaction, vendorRejectionReason);
    setTimeout(() => closeModal(), 400);
  };

  const handleCardProp65DrawerOpen = () => {
    setCardProp65PurchaseOrder(purchaseOrder);
    setIsCardProp65DrawerOpen(true);
  };

  const handleCardProp65DrawerClose = () => {
    setIsCardProp65DrawerOpen(false);
    setCardProp65PurchaseOrder(null);
  };

  const handleCardProp65Save = async (
    prop65Values: Record<string, 'yes' | 'no'>,
  ) => {
    try {
      const poNumber = purchaseOrder.po_number;

      const updates = purchaseOrder.purchase_order_lines
        .filter((line) => prop65Values[line.po_line_id] !== undefined)
        .map((line) => ({
          lineId: Number(line.po_line_id),
          prop65: prop65Values[line.po_line_id] === 'yes',
        }));

      if (updates.length === 0) {
        toast('No changes to save.', { type: 'info' });
        handleCardProp65DrawerClose();
        return;
      }

      await setProp65({
        userId,
        poNumber,
        updates,
      });
      toast('CA Prop 65 warning successfully updated.', { type: 'success' });
      handleCardProp65DrawerClose();
      setTimeout(() => {
        queryClient.invalidateQueries(['purchase-order', userId]);
        queryClient.prefetchQuery(['purchase-order', poNumber], () =>
          UserAPI.getPurchaseOrder(userId, poNumber),
        );
      }, 300);
    } catch (errorProp65) {
      toast('Failed to update CA Prop 65 warning.', { type: 'error' });
    }
  };

  const handleOpenRevisionDrawer = () => {
    setIsRevisionModalOpen(true);
  };

  return (
    <>
      {details ? (
        <PoDetails
          purchaseOrder={purchaseOrder}
          isMobile={isMobile}
          userId={userId}
          handleAcceptModal={handleAcceptModal}
          handleRejectModal={handleRejectModal}
          handlePrintUnitLabelModal={handlePrintUnitLabelModal}
          handlePrintHangTagModal={handlePrintHangTagModal}
          handleOpenRevisionDrawer={handleOpenRevisionDrawer}
          onPoSubStageUpdate={onPoSubStageUpdate}
          perms={perms}
          user={user}
          onProp65DrawerOpen={onProp65DrawerOpen}
        />
      ) : (
        <div style={{ position: 'relative' }}>
          {purchaseOrder.isLoading && (
            <div className="absolute inset-0 w-full h-full bg-white/70 z-10 flex items-center justify-center">
              <Icon.LoadingCircle />
            </div>
          )}
          <Stack
            direction="col"
            className={cn(
              'min-w-[450px] relative group rounded-lg border border-neutral-300 bg-neutral-0 shadow-[0_2px_6px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_6px_rgba(0,0,0,0.12)] overflow-hidden',
              selected && 'ring ring-primary-700',
            )}
          >
            <Stack gap="md" direction="row">
              <PoImage purchaseOrder={purchaseOrder} />
              <Stack direction="row">
                <PoCardInfo
                  perms={perms}
                  purchaseOrder={purchaseOrder}
                  userId={userId}
                  handleAcceptModal={handleAcceptModal}
                  user={user}
                />
                <PoCardHeaderButtons
                  handleAcceptModal={handleAcceptModal}
                  handleRejectModal={handleRejectModal}
                  handleOpenRevisionDrawer={handleOpenRevisionDrawer}
                  userId={userId}
                  purchaseOrder={purchaseOrder}
                  selected={selected}
                  onSelect={onSelect}
                  perms={perms}
                  user={user}
                />
              </Stack>
            </Stack>
            <PoCardFooter
              perms={perms}
              purchaseOrder={purchaseOrder}
              handleAcceptModal={handleAcceptModal}
              handleRejectModal={handleRejectModal}
              handlePrintUnitLabelModal={handlePrintUnitLabelModal}
              handlePrintHangTagModal={handlePrintHangTagModal}
              user={user}
              onProp65DrawerOpen={handleCardProp65DrawerOpen}
            />
          </Stack>
        </div>
      )}

      <UnitLabelModal
        open={state.openPrintUnitLabel}
        onClose={closeModal}
        onConfirm={confirmPrintModal}
        poInformation={state.printPOInformation}
        userId={userId}
      />
      <HangtagModal
        open={state.openPrintHangtag}
        onClose={closeModal}
        onConfirm={confirmPrintModal}
        poInformation={state.printPOInformation}
        userId={userId}
      />
      <ConfirmationModal
        open={state.openAccept}
        onClose={closeModal}
        onConfirm={acceptFromModal}
        loading={loading}
      />
      <ConfirmationModal
        open={state.openReject}
        messageTitle="Add A Rejection Reason"
        onClose={closeModal}
        onConfirm={rejectFromModal}
        loading={loading}
        withMessage
      />

      {cardProp65PurchaseOrder && (
        <Prop65Drawer
          isOpen={isCardProp65DrawerOpen}
          onClose={handleCardProp65DrawerClose}
          onSave={handleCardProp65Save}
          loading={loading}
          purchaseOrder={cardProp65PurchaseOrder}
        />
      )}
      <PurchaseOrderRevisionDrawer
        isOpen={isRevisionModalOpen}
        onClose={() => setIsRevisionModalOpen(false)}
        purchaseOrder={purchaseOrder}
        userId={userId}
      />
    </>
  );
}
